import React, { useState } from "react";
import {
  useListDisputes,
  useUpdateDispute,
  useCreateDispute,
  useGetMe,
  getListDisputesQueryKey,
  listDisputes,
  type Dispute,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Download, FileSpreadsheet, Link, Loader2, Plus, Send } from "lucide-react";
import { formatZAR } from "@/lib/formatters";
import { TablePagination } from "@/components/TablePagination";

const RESOLVABLE = ["pending", "action_required"];
const PAGE_SIZE = 10;

export function DisputesList({ embedded = false }: { embedded?: boolean } = {}) {
  const { data: me } = useGetMe();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useListDisputes({
    page,
    pageSize: PAGE_SIZE,
  });
  const [selected, setSelected] = useState<Dispute | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const { toast } = useToast();

  const isAgent = me?.role === "agent";
  const canResolve =
    me?.role === "poc_support" ||
    me?.role === "finance" ||
    me?.role === "admin";
  const canReport = me?.role === "admin" || me?.role === "finance";
  const [reportLoading, setReportLoading] = useState<"excel" | "link" | "send" | null>(null);
  const [shareLink, setShareLink] = useState<string | null>(null);

  if (isLoading)
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  if (error || !data)
    return <div className="p-6 text-red-500">Failed to load disputes.</div>;

  const disputes = data.items;

  const exportCsv = async () => {
    const headers = [
      "Reference",
      "Subject",
      "Agent",
      "Order ID",
      "Amount (ZAR)",
      "Status",
      "Resolution notes",
      "Date raised",
    ];
    const all: Dispute[] = [];
    let p = 1;
    for (;;) {
      const res = await listDisputes({ page: p, pageSize: 100 });
      all.push(...res.items);
      if (all.length >= res.total || res.items.length === 0) break;
      p += 1;
    }
    const rows = all.map((d) => [
      d.reference,
      d.subject,
      d.agentName ?? "",
      d.orderId != null ? String(d.orderId) : "",
      d.amountCents != null ? (d.amountCents / 100).toFixed(2) : "",
      d.status,
      d.resolutionNotes ?? "",
      d.createdAt ? new Date(d.createdAt).toISOString().slice(0, 10) : "",
    ]);
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => escape(String(c))).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `disputes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = async () => {
    setReportLoading("excel");
    try {
      const res = await fetch("/api/disputes/report/export");
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mooya-dispute-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ variant: "destructive", title: "Export failed", description: "Could not generate the report." });
    } finally {
      setReportLoading(null);
    }
  };

  const getShareLink = async () => {
    setReportLoading("link");
    try {
      const res = await fetch("/api/disputes/report/token");
      if (!res.ok) throw new Error(await res.text());
      const data = (await res.json()) as { url: string };
      setShareLink(data.url);
    } catch {
      toast({ variant: "destructive", title: "Failed", description: "Could not generate shareable link." });
    } finally {
      setReportLoading(null);
    }
  };

  const sendToOpenserve = async () => {
    setReportLoading("send");
    try {
      const res = await fetch("/api/disputes/report/send", { method: "POST" });
      const data = (await res.json()) as { ok: boolean; count?: number; error?: string };
      if (!res.ok || !data.ok) throw new Error(data.error ?? "Unknown error");
      toast({ title: "Report sent", description: `Dispute report dispatched to Openserve (${data.count} disputes).` });
    } catch (err) {
      toast({ variant: "destructive", title: "Send failed", description: err instanceof Error ? err.message : "Unknown error" });
    } finally {
      setReportLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div
        className={`flex items-center ${embedded ? "justify-end" : "justify-between"}`}
      >
        {!embedded && (
          <h1 className="text-2xl font-bold text-[#0B132B]">Disputes</h1>
        )}
        <div className="flex items-center gap-2">
          {isAgent && (
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Raise dispute
            </Button>
          )}
          <Button
            onClick={exportCsv}
            variant="outline"
            disabled={disputes.length === 0}
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {canReport && (
        <Card className="border-0 shadow-sm bg-gray-50">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-[#0B132B] mb-3 uppercase tracking-wider">Openserve Report</p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportExcel}
                disabled={reportLoading !== null}
                className="border-gray-200"
              >
                {reportLoading === "excel" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}
                Export Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={getShareLink}
                disabled={reportLoading !== null}
                className="border-gray-200"
              >
                {reportLoading === "link" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Link className="w-3.5 h-3.5 mr-1.5" />}
                Get shareable link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={sendToOpenserve}
                disabled={reportLoading !== null}
                className="border-[#E6007E]/40 text-[#E6007E] hover:bg-[#E6007E]/5"
              >
                {reportLoading === "send" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Send to Openserve
              </Button>
            </div>
            {shareLink && (
              <div className="mt-3 p-2 bg-white border border-gray-200 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Shareable link (7-day expiry):</p>
                <a
                  href={shareLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#E6007E] hover:underline break-all"
                >
                  {shareLink}
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">
            {isAgent ? "Your disputes" : "Consolidated disputes"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Raised</TableHead>
                {canResolve && (
                  <TableHead className="text-right">Action</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {disputes.map((dispute) => (
                <TableRow key={dispute.id}>
                  <TableCell className="font-medium font-mono text-xs">
                    {dispute.reference}
                  </TableCell>
                  <TableCell>{dispute.subject}</TableCell>
                  <TableCell>{dispute.agentName || "-"}</TableCell>
                  <TableCell>
                    {dispute.amountCents != null
                      ? formatZAR(dispute.amountCents)
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={dispute.status} />
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">
                    {dispute.createdAt
                      ? new Date(dispute.createdAt).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  {canResolve && (
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelected(dispute)}
                      >
                        {RESOLVABLE.includes(dispute.status)
                          ? "Resolve"
                          : "View"}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {disputes.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={canResolve ? 7 : 6}
                    className="text-center p-8 text-gray-500"
                  >
                    No disputes found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            total={data.total}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      {canResolve && (
        <ResolveDialog dispute={selected} onClose={() => setSelected(null)} />
      )}
      {isAgent && (
        <CreateDisputeDialog open={createOpen} onOpenChange={setCreateOpen} />
      )}
    </div>
  );
}

function ResolveDialog({
  dispute,
  onClose,
}: {
  dispute: Dispute | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [notes, setNotes] = useState("");
  const update = useUpdateDispute();
  const closed = dispute ? !RESOLVABLE.includes(dispute.status) : false;

  const decide = async (
    status: "approved" | "rejected" | "action_required",
  ) => {
    if (!dispute) return;
    try {
      await update.mutateAsync({
        id: dispute.id,
        data: { status, resolutionNotes: notes.trim() || undefined },
      });
      queryClient.invalidateQueries({ queryKey: getListDisputesQueryKey() });
      toast({
        title: "Dispute updated",
        description: `${dispute.reference} marked as ${status.replace(/_/g, " ")}.`,
      });
      setNotes("");
      onClose();
    } catch {
      toast({
        title: "Could not update dispute",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={!!dispute} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[480px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">
            {dispute?.subject}
          </DialogTitle>
        </DialogHeader>
        {dispute && (
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-gray-500">
                {dispute.reference}
              </span>
              <StatusBadge status={dispute.status} />
            </div>
            {dispute.description && (
              <p className="text-sm text-gray-600">{dispute.description}</p>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500">Agent</p>
                <p className="font-medium text-[#0B132B]">
                  {dispute.agentName || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Amount</p>
                <p className="font-medium text-[#0B132B]">
                  {dispute.amountCents != null
                    ? formatZAR(dispute.amountCents)
                    : "—"}
                </p>
              </div>
            </div>
            {dispute.resolutionNotes && (
              <div>
                <p className="text-xs text-gray-500">Resolution notes</p>
                <p className="text-sm text-[#0B132B]">
                  {dispute.resolutionNotes}
                </p>
              </div>
            )}
            {!closed && (
              <div className="space-y-1.5">
                <Label>Resolution notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Explain the outcome"
                  rows={3}
                />
              </div>
            )}
          </div>
        )}
        {!closed && (
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              onClick={() => decide("action_required")}
              disabled={update.isPending}
              variant="outline"
              className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50"
            >
              Needs info
            </Button>
            <Button
              onClick={() => decide("rejected")}
              disabled={update.isPending}
              variant="outline"
              className="flex-1 border-red-200 text-red-700 hover:bg-red-50"
            >
              Reject
            </Button>
            <Button
              onClick={() => decide("approved")}
              disabled={update.isPending}
              className="flex-1 bg-[#00A859] hover:bg-[#008f4c] text-white"
            >
              {update.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Approve"
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateDisputeDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const create = useCreateDispute();

  const reset = () => {
    setSubject("");
    setDescription("");
    setAmount("");
  };

  const amountTrimmed = amount.trim();
  const amountValid =
    amountTrimmed === "" ||
    (Number.isFinite(Number(amountTrimmed)) && Number(amountTrimmed) >= 0);
  const valid = subject.trim().length > 0 && amountValid;

  const submit = async () => {
    if (!valid) return;
    try {
      await create.mutateAsync({
        data: {
          subject: subject.trim(),
          description: description.trim() || undefined,
          amountCents:
            amountTrimmed === ""
              ? undefined
              : Math.round(Number(amountTrimmed) * 100),
        },
      });
      queryClient.invalidateQueries({ queryKey: getListDisputesQueryKey() });
      toast({
        title: "Dispute submitted",
        description: "The support team will review your dispute.",
      });
      reset();
      onOpenChange(false);
    } catch {
      toast({
        title: "Could not submit dispute",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">Raise a dispute</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-gray-500">
          Describe the issue and our support team will investigate.
        </p>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Missing commission on order"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add any details that will help us investigate"
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Disputed amount (optional, ZAR)</Label>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="font-mono"
              inputMode="decimal"
              placeholder="0.00"
            />
            {!amountValid && (
              <p className="text-xs text-red-600">Enter a valid amount.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!valid || create.isPending}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
          >
            {create.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Submit dispute"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
